import IDream from "motw_core/Entity/IDream";
import { IKeyValue } from "motw_core/Entity/IKeyValue";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import * as React from "react";
import { EEventType } from "source/app/services/Events/EEventType";
import { SubscribeToEvent } from "source/app/services/Events/Events";
import { AreEqual } from "source/app/services/Shared/ReactHelpers";
import { ReadState } from "source/app/services/UI/State/ReadState";
import { DreamRow } from "../DreamRow/DreamRow";
import { IDreamsState } from "../IDreamsState";
import * as styles from "./DreamsList.css";
import IconWithText from "source/app/services/Shared/IconWithText/IconWithText";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";

function DreamsListComponent({ state }: { state: IDreamsState }) {
    const [currentEnergy, setCurrentEnergy] = React.useState<number>(3);
    const [shouldShowAllRows, setShouldShowAllRows] =
        React.useState<boolean>(false);

    React.useEffect(() => {
        const updateEnergy = () => setCurrentEnergy(GetCurrentEnergy());

        SubscribeToEvent(
            EEventType.ShowCurrentEnergy_valueChanged,
            updateEnergy
        );
    }, []);

    const memoizedFilteredData = React.useMemo(
        () => state.filteredData,
        [state.filteredData]
    );

    const memoizedSortedData = React.useMemo(
        () => state.sortedData,
        [state.sortedData]
    );

    const isTooLarge = memoizedFilteredData.length > 100;
    const shouldShowRows = !isTooLarge || shouldShowAllRows;

    React.useEffect(() => {
        if (!isTooLarge && shouldShowAllRows) {
            setShouldShowAllRows(false);
        }
    }, [isTooLarge, shouldShowAllRows]);

    const renderDreamRow = (row: IDream, index: number) => {
        const drawBottomBorder = ShouldDrawBottomBorder(
            memoizedFilteredData,
            row,
            index
        );

        const isRandomDream =
            state.randomDream !== undefined && row.id === state.randomDream.id;

        return (
            <DreamRow
                key={row.id} // Using row.id instead of index to avoid incorrect renders
                dream={row}
                sortedData={memoizedSortedData}
                drawBottomBorder={drawBottomBorder}
                isRandomDream={isRandomDream}
                selectedDifficulty={state.selectedDifficulty}
                currentEnergy={currentEnergy}
                searchText={state.searchText}
            />
        );
    };

    return (
        <div id="calcTarget_dreamsList" className={styles.container}>
            {!shouldShowRows ? (
                <TooManyDreams
                    count={memoizedFilteredData.length}
                    onShowAll={() => setShouldShowAllRows(true)}
                />
            ) : (
                <div className={styles.dreamRows}>
                    {memoizedFilteredData.map(renderDreamRow)}
                </div>
            )}
        </div>
    );
}

export const DreamsList = React.memo(
    DreamsListComponent,
    (prevProps, nextProps) => AreEqual(prevProps, nextProps)
);

const ShouldDrawBottomBorder = (
    filteredData: IDream[],
    row: IDream,
    index: number
) => {
    const isAtLastRow = index === filteredData.length - 1;

    if (isAtLastRow) {
        return false;
    }

    const currentDifficulty = row.difficulty;
    const nextDifficulty = filteredData[index + 1].difficulty;

    if (currentDifficulty !== nextDifficulty) {
        return true;
    }

    return false;
};

function GetCurrentEnergy() {
    const currentEnergyKeyValue = ReadState<IKeyValue>(
        ESharedFunction.ShowCurrentEnergy_currentValue
    );

    const currentEnergy = currentEnergyKeyValue
        ? Number.parseInt(currentEnergyKeyValue.value)
        : 3;
    return currentEnergy;
}

function TooManyDreams({
    count,
    onShowAll,
}: {
    count: number;
    onShowAll: () => void;
}) {
    const text = `Click to show all ${count.toString()} items`;

    return (
        <div className={styles.dreamRows}>
            <IconWithText
                iconSuffix={EGlyphicon["resize-full"]}
                displayAsBlock={false}
                whenClicked={onShowAll}
            >
                {text}
            </IconWithText>
        </div>
    );
}
